import type { Node, Edge } from "@xyflow/react";

export type FlowchartNodeType = "process" | "decision" | "terminal";

export interface FlowchartNodeData {
    label: string;
    nodeType: FlowchartNodeType;
    color?: string;
    [key: string]: unknown;
}

export const COLOR_PALETTE: { bg: string; text: string }[] = [
    { bg: "#ef4444", text: "#ffffff" },
    { bg: "#f97316", text: "#ffffff" },
    { bg: "#f59e0b", text: "#422006" },
    { bg: "#eab308", text: "#422006" },
    { bg: "#84cc16", text: "#1a2e05" },
    { bg: "#22c55e", text: "#052e16" },
    { bg: "#10b981", text: "#022c22" },
    { bg: "#14b8a6", text: "#042f2e" },
    { bg: "#06b6d4", text: "#083344" },
    { bg: "#0ea5e9", text: "#082f49" },
    { bg: "#3b82f6", text: "#ffffff" },
    { bg: "#6366f1", text: "#ffffff" },
    { bg: "#8b5cf6", text: "#ffffff" },
    { bg: "#a855f7", text: "#ffffff" },
    { bg: "#d946ef", text: "#ffffff" },
    { bg: "#ec4899", text: "#ffffff" },
    { bg: "#fca5a5", text: "#7f1d1d" },
    { bg: "#fdba74", text: "#7c2d12" },
    { bg: "#fde047", text: "#422006" },
    { bg: "#bef264", text: "#1a2e05" },
    { bg: "#86efac", text: "#052e16" },
    { bg: "#6ee7b7", text: "#022c22" },
    { bg: "#5eead4", text: "#042f2e" },
    { bg: "#7dd3fc", text: "#082f49" },
    { bg: "#93c5fd", text: "#1e3a5f" },
    { bg: "#a5b4fc", text: "#312e81" },
    { bg: "#c4b5fd", text: "#3b0764" },
    { bg: "#d8b4fe", text: "#3b0764" },
    { bg: "#f0abfc", text: "#701a75" },
    { bg: "#f9a8d4", text: "#831843" },
    { bg: "#d6d3d1", text: "#292524" },
    { bg: "#9ca3af", text: "#111827" },
];

export function textColorForBg(hex: string | undefined): string | undefined {
    if (!hex) return undefined;
    const entry = COLOR_PALETTE.find((c) => c.bg.toLowerCase() === hex.toLowerCase());
    if (entry) return entry.text;
    const c = hex.replace("#", "");
    const r = parseInt(c.substring(0, 2), 16);
    const g = parseInt(c.substring(2, 4), 16);
    const b = parseInt(c.substring(4, 6), 16);
    if (isNaN(r) || isNaN(g) || isNaN(b)) return undefined;
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    return luminance > 0.55 ? "#000000" : "#ffffff";
}

interface DiagramMeta {
    positions: Record<string, { x: number; y: number }>;
}

function parseMetaComment(lines: string[]): DiagramMeta | null {
    for (const line of lines) {
        const m = /^%%\s*meta:\s*(.+)$/.exec(line.trim());
        if (m) {
            try {
                return JSON.parse(m[1]) as DiagramMeta;
            } catch {
                return null;
            }
        }
    }
    return null;
}

function buildMetaComment(nodes: Node<FlowchartNodeData>[]): string {
    const positions: Record<string, { x: number; y: number }> = {};
    for (const n of nodes) {
        positions[n.id] = {
            x: Math.round(n.position.x),
            y: Math.round(n.position.y),
        };
    }
    return `%% meta: ${JSON.stringify({ positions })}`;
}

interface ParsedNode {
    id: string;
    label: string;
    type: FlowchartNodeType;
    color?: string;
}

interface ParsedEdge {
    source: string;
    target: string;
    label: string;
}

export function parseMermaidToReactFlow(mermaid: string): {
    nodes: Node<FlowchartNodeData>[];
    edges: Edge[];
} {
    const lines = mermaid.split("\n").map((l) => l.trim()).filter(Boolean);

    const parsedNodes = new Map<string, ParsedNode>();
    const parsedEdges: ParsedEdge[] = [];
    const nodeColors = new Map<string, string>();

    const meta = parseMetaComment(lines);

    let startIdx = 0;
    for (let i = 0; i < lines.length; i++) {
        if (/^(flowchart|graph)\s+(TD|TB|LR|RL|BT)/i.test(lines[i])) {
            startIdx = i + 1;
            break;
        }
    }

    const nodeShapeRegex =
        /^([A-Za-z_][A-Za-z0-9_]*)\s*(\(\[(.+?)\]\)|\[(.+?)\]|\{(.+?)\})\s*$/;

    const edgeRegex =
        /^([A-Za-z_][A-Za-z0-9_]*)\s*(?:--\s+(.+?)\s+)?-->(?:\|(.+?)\|)?\s*([A-Za-z_][A-Za-z0-9_]*)(?:\s*(\(\[(.+?)\]\)|\[(.+?)\]|\{(.+?)\}))?$/;

    const styleRegex = /^style\s+([A-Za-z_][A-Za-z0-9_]*)\s+(.+)$/;

    function ensureNode(id: string) {
        if (!parsedNodes.has(id)) {
            parsedNodes.set(id, { id, label: id, type: "process" });
        }
    }

    for (let i = startIdx; i < lines.length; i++) {
        const line = lines[i];

        if (/^%%/.test(line)) continue;

        const sm = styleRegex.exec(line);
        if (sm) {
            const nodeId = sm[1];
            const props = sm[2];
            const fillMatch = /fill:\s*(#[0-9a-fA-F]{3,8})/.exec(props);
            if (fillMatch) {
                nodeColors.set(nodeId, fillMatch[1]);
            }
            continue;
        }

        const nm = nodeShapeRegex.exec(line);
        if (nm) {
            const id = nm[1];
            const stadiumLabel = nm[3];
            const rectLabel = nm[4];
            const diamondLabel = nm[5];

            let type: FlowchartNodeType = "process";
            let label = id;

            if (stadiumLabel !== undefined) {
                type = "terminal";
                label = stadiumLabel;
            } else if (diamondLabel !== undefined) {
                type = "decision";
                label = diamondLabel;
            } else if (rectLabel !== undefined) {
                type = "process";
                label = rectLabel;
            }

            parsedNodes.set(id, { id, label, type });
            continue;
        }

        const em = edgeRegex.exec(line);
        if (em) {
            const sourceId = em[1];
            const edgeLabelDash = em[2] || "";
            const edgeLabelPipe = em[3] || "";
            const targetId = em[4];
            const edgeLabel = edgeLabelPipe || edgeLabelDash;

            ensureNode(sourceId);
            ensureNode(targetId);

            const targetStadium = em[6];
            const targetRect = em[7];
            const targetDiamond = em[8];

            if (targetStadium !== undefined) {
                parsedNodes.set(targetId, { id: targetId, label: targetStadium, type: "terminal" });
            } else if (targetDiamond !== undefined) {
                parsedNodes.set(targetId, { id: targetId, label: targetDiamond, type: "decision" });
            } else if (targetRect !== undefined) {
                parsedNodes.set(targetId, { id: targetId, label: targetRect, type: "process" });
            }

            parsedEdges.push({ source: sourceId, target: targetId, label: edgeLabel });
            continue;
        }
    }

    for (const [nodeId, color] of nodeColors) {
        const node = parsedNodes.get(nodeId);
        if (node) {
            node.color = color;
        }
    }

    const COL_WIDTH = 200;
    const ROW_HEIGHT = 100;
    const COLS = 3;
    const nodeArray = Array.from(parsedNodes.values());

    const nodes: Node<FlowchartNodeData>[] = nodeArray.map((n, idx) => {
        const savedPos = meta?.positions?.[n.id];
        return {
            id: n.id,
            type: n.type,
            position: savedPos
                ? { x: savedPos.x, y: savedPos.y }
                : {
                      x: (idx % COLS) * COL_WIDTH + 50,
                      y: Math.floor(idx / COLS) * ROW_HEIGHT + 50,
                  },
            data: { label: n.label, nodeType: n.type, color: n.color },
        };
    });

    const edges: Edge[] = parsedEdges.map((e, idx) => ({
        id: `e-${idx}`,
        source: e.source,
        target: e.target,
        label: e.label || undefined,
        type: "smoothstep",
    }));

    return { nodes, edges };
}

const shapeOpen: Record<FlowchartNodeType, string> = {
    process: "[",
    decision: "{",
    terminal: "([",
};
const shapeClose: Record<FlowchartNodeType, string> = {
    process: "]",
    decision: "}",
    terminal: "])",
};

export function reactFlowToMermaid(
    nodes: Node<FlowchartNodeData>[],
    edges: Edge[],
): string {
    const lines: string[] = ["flowchart TD"];

    for (const n of nodes) {
        const type = n.data.nodeType ?? "process";
        const label = n.data.label || n.id;
        lines.push(`    ${n.id}${shapeOpen[type]}${label}${shapeClose[type]}`);
    }

    for (const e of edges) {
        if (e.label) {
            lines.push(`    ${e.source} -->|${e.label}| ${e.target}`);
        } else {
            lines.push(`    ${e.source} --> ${e.target}`);
        }
    }

    for (const n of nodes) {
        if (n.data.color) {
            lines.push(`    style ${n.id} fill:${n.data.color}`);
        }
    }

    lines.push(`    ${buildMetaComment(nodes)}`);

    return lines.join("\n");
}
